import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowDown, ArrowUp } from 'lucide-react';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Clickable, Sub, Table } from '../../performance-operations/styles';
import {
  HIGHER_IS_BETTER, formatNum, isSuppressed, metricByCode, metricText, reasonText, unitLabel,
} from '../../performance-results/config';

// PM analitik P2 — E10 orta alan: organizasyon satırları × seçilen metrik sütunları ısı haritası (varsayılan M02, M06, M09, M07,
// M08). Hücre tonu birimin başlık satırına (şirket / üst birim) göre farkıdır — renk her zaman değer metni ve ok ikonuyla
// birlikte; gizli hücre "Gizli" yazar ve renklenmez (gizli değer tonla sezdirilmez). Satır seçimi birim çekmecesini açar.
export const HEATMAP_ROWS = 20;

const Cell = styled.td`
  position: relative;
  text-align: center;
  &::before {
    content: '';
    position: absolute;
    inset: 2px;
    border-radius: 4px;
    background: ${({ theme, $tone }) => ($tone === 'good' ? theme.colors.secondary.main : $tone === 'bad' ? theme.colors.auxiliar.danger : 'transparent')};
    opacity: ${({ $alpha }) => $alpha || 0};
  }
  & > span {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }
`;

const N = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
`;

const Flag = styled.span`
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  margin-left: 4px;
  display: inline-flex;
  vertical-align: middle;
`;

const Callout = styled.div`
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgColor};
  border: 1px solid ${({ theme }) => theme.colors.auxiliar.danger};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

/** Hücre tonu ve yoğunluğu (0–1): yüzdede 20 puan, hedef puanında 20 puan, ortalama puanda ölçek aralığının %25'i tam ton. */
export function cellTone(code, unitMetric, headerMetric, scaleMin, scaleMax) {
  const higher = HIGHER_IS_BETTER[code];
  if (higher === null || higher === undefined) return { tone: 'neutral', alpha: 0 };
  if (!unitMetric || !headerMetric || isSuppressed(unitMetric) || isSuppressed(headerMetric)) return { tone: 'neutral', alpha: 0 };
  if (unitMetric.value === null || unitMetric.value === undefined || headerMetric.value === null || headerMetric.value === undefined) return { tone: 'neutral', alpha: 0 };
  const gap = Number(unitMetric.value) - Number(headerMetric.value);
  if (gap === 0) return { tone: 'neutral', alpha: 0, gap };
  const range = code === 'M06' ? Math.max(Number(scaleMax || 0) - Number(scaleMin || 0), 1) * 0.25 : 20;
  const intensity = Math.min(1, Math.abs(gap) / range);
  return { tone: (gap > 0) === higher ? 'good' : 'bad', alpha: 0.1 + 0.35 * intensity, gap };
}

export default function UnitHeatmap({
  data, loading, error, onRetry, codes, onOpen,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [table, setTable] = useState(false);
  const units = (data?.units || []).slice(0, HEATMAP_ROWS);
  const header = data?.header;
  const critical = data?.criticalUnit;

  const renderCell = (unit, code, colored) => {
    const m = metricByCode(unit.metrics, code);
    const h = header ? metricByCode(header.metrics, code) : null;
    const { tone, alpha } = colored ? cellTone(code, m, h, data?.scaleMin, data?.scaleMax) : { tone: 'neutral', alpha: 0 };
    const Icon = tone === 'good' ? (HIGHER_IS_BETTER[code] ? ArrowUp : ArrowDown) : tone === 'bad' ? (HIGHER_IS_BETTER[code] ? ArrowDown : ArrowUp) : null;
    return (
      <Cell key={code} $tone={tone} $alpha={alpha} data-cell={`${unit.key}-${code}`} data-tone={tone}>
        <span>
          {Icon ? <Icon size={11} aria-hidden="true" /> : null}
          {metricText(t, m, lang)}
          {m && !isSuppressed(m) && m.sampleN !== null && m.sampleN !== undefined && m.kind === 'result' ? <N>{` n=${formatNum(m.sampleN, lang, 0)}`}</N> : null}
        </span>
      </Cell>
    );
  };

  return (
    <PanelCard
      panelKey="heatmap" title={t('performance.orgComparison.heatmap.title')} span={12}
      loading={loading} error={error} onRetry={onRetry} empty={!!data && units.length === 0}
      emptyText={t('performance.orgComparison.heatmap.empty')}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {critical ? (
        <Callout data-critical={critical.key}>
          <strong>{t('performance.orgComparison.criticalUnit', { name: unitLabel(t, critical) })}</strong>
          {' — '}
          {critical.criticalReasons.map((r) => reasonText(t, r, lang)).join(' · ')}
        </Callout>
      ) : null}
      <Sub data-note="legend">{t('performance.orgComparison.heatmap.legend')}</Sub>
      <Table data-view={table ? 'table' : 'heatmap'}>
        <thead>
          <tr>
            <th>{t('performance.results.analytics.unit.col.unit')}</th>
            <th>{t('performance.results.analytics.unit.col.resultN')}</th>
            {codes.map((c) => <th key={c} style={{ textAlign: 'center' }}>{t(`performance.results.analytics.metric.${c}`)}</th>)}
          </tr>
        </thead>
        <tbody>
          {header ? (
            <tr data-row="header">
              <td><strong>{unitLabel(t, header)}</strong></td>
              <td>{formatNum(header.resultN, lang, 0)}</td>
              {codes.map((c) => renderCell(header, c, false))}
            </tr>
          ) : null}
          {units.map((u) => (
            <tr key={u.key} data-row={u.key}>
              <td title={unitLabel(t, u)}>
                <Clickable type="button" onClick={() => onOpen(u)} data-action="open-unit">{unitLabel(t, u)}</Clickable>
                {u.critical ? <Flag title={t('performance.orgComparison.criticalFlag')}><AlertTriangle size={12} aria-label={t('performance.orgComparison.criticalFlag')} /></Flag> : null}
              </td>
              <td>{formatNum(u.resultN, lang, 0)}</td>
              {codes.map((c) => renderCell(u, c, !table))}
            </tr>
          ))}
        </tbody>
      </Table>
      {data && data.units.length > HEATMAP_ROWS ? <Sub data-note="more">{t('performance.orgComparison.heatmap.more', { count: HEATMAP_ROWS })}</Sub> : null}
    </PanelCard>
  );
}
